import { withWorkspaceLoadingOverlay } from '../js/core/ui/workspace_loading_overlay.js?v=20260826a';
import {
  clearCurrentUser,
  setCurrentUser
} from '../js/core/user/current_user.js?v=20260722a';
import { initAppVersionBadge } from '../js/core/app/app_version.js?v=20260706d';
import { escapeHtml } from '../js/core/utils/html_utils.js?v=20260706d';
import { flagIconSvg } from '../js/core/utils/flag_icons.js?v=20260730a';

const GATE_LANGUAGE_STORAGE_KEY = 'ks-studio-gate-language-v1';
// Getrennt von GATE_LANGUAGE_STORAGE_KEY (das merkt sich dauerhaft, welche
// Flagge auf dem Login-Bildschirm zuletzt aktiv war -- rein kosmetisch,
// unabhaengig vom Login). Dieser Key wird NUR bei einem tatsaechlichen
// Login-Absenden gesetzt und von app_options_controller.js sofort nach dem
// Lesen wieder geloescht (Einmal-Konsum) -- so wirkt die Gate-Sprache nur
// GENAU EINMAL, im Moment des Logins, auf die Session-Sprache. Ohne diese
// Trennung wuerde die alte, dauerhaft gemerkte Gate-Sprache bei JEDEM
// Neuladen/Session-Wiederherstellen erneut gewinnen und eine spaeter in den
// Optionen bewusst gewaehlte Sprache staendig ueberschreiben.
const GATE_LANGUAGE_FRESH_LOGIN_KEY = 'ks-studio-gate-language-fresh-login-v1';

// Keys mirror the 'login' Karte seeded in backend/schema.sql (bereich='modal',
// karte='login') — fetched pre-login via /api/auth/gate-labels since the
// normal /api/i18n/* endpoints require a session. Local short keys here are
// just for readability; GATE_LABEL_DB_KEYS maps them to the real DB schluessel.
const GATE_LABEL_DB_KEYS = {
  loginName: 'modal.login.label.login_name',
  passwort: 'modal.login.label.passwort',
  angemeldetBleiben: 'modal.login.label.angemeldet_bleiben',
  sessionWirdGeprueft: 'modal.login.wert.session_wird_geprueft',
  loginWirdGeprueft: 'modal.login.wert.login_wird_geprueft',
  loginUngueltig: 'modal.login.wert.login_ungueltig',
  vorbereitungFehler: 'modal.login.wert.vorbereitung_fehler',
  ladenFehler: 'modal.login.wert.laden_fehler',
  kontaktFehler: 'modal.login.wert.kontakt_fehler',
  einloggen: 'modal.login.button.einloggen',
  passwortVergessen: 'modal.login.button.passwort_vergessen',
  zugangAnfordern: 'modal.login.button.zugang_anfordern',
  zugangAnfordernBetreff: 'modal.login.wert.zugang_anfordern_betreff',
  zugangAnfordernText: 'modal.login.wert.zugang_anfordern_text',
  passwortVergessenBetreff: 'modal.login.wert.passwort_vergessen_betreff',
  passwortVergessenText: 'modal.login.wert.passwort_vergessen_text'
};

const GATE_LABEL_FALLBACK_DE = {
  loginName: 'Login-Name oder E-Mail',
  passwort: 'Passwort',
  angemeldetBleiben: 'Angemeldet bleiben',
  sessionWirdGeprueft: 'Session wird geprüft...',
  loginWirdGeprueft: 'Login wird geprüft...',
  loginUngueltig: 'Login oder Passwort ungültig.',
  vorbereitungFehler: 'KS Studio konnte nicht vorbereitet werden.',
  ladenFehler: 'KS Studio konnte nicht geladen werden.',
  kontaktFehler: 'Kontakt konnte nicht geladen werden. Bitte erneut versuchen.',
  einloggen: 'Einloggen',
  passwortVergessen: 'Passwort vergessen',
  zugangAnfordern: 'Zugang anfordern',
  zugangAnfordernBetreff: 'Zugang für KS Studio anfordern',
  zugangAnfordernText: 'Hallo, ich möchte gerne Zugriff auf KS Studio erhalten. Danke',
  passwortVergessenBetreff: 'Passwort zurücksetzen für KS Studio',
  passwortVergessenText: 'Hallo, ich habe mein Passwort für KS Studio vergessen. Bitte setzen Sie mein Passwort zurück. Danke'
};

let appStarted = false;
let appMounted = false;
let gateLanguageCode = readStoredGateLanguage();
let gateLanguages = [];
let gateLabels = { ...GATE_LABEL_FALLBACK_DE };
let currentMessageKey = null;

function readStoredGateLanguage() {
  try {
    return String(window.localStorage?.getItem(GATE_LANGUAGE_STORAGE_KEY) || '').trim().toUpperCase() || 'DE';
  } catch (_err) {
    return 'DE';
  }
}

function storeGateLanguage(code) {
  try {
    window.localStorage?.setItem(GATE_LANGUAGE_STORAGE_KEY, code);
  } catch (_err) {
    // Gate bleibt auch ohne Browser-Speicher nutzbar, Sprache wird dann nicht ueber einen Reload hinweg gemerkt.
  }
}

// gt(): Pendant zu i18n_service.js' t(), aber unabhaengig davon — das Gate
// laeuft vor initI18n()/i18next (siehe app_bootstrap.js), und die normalen
// /api/i18n/* Endpunkte verlangen eine Session, die es hier noch nicht gibt.
function gt(key) {
  return gateLabels[key] || GATE_LABEL_FALLBACK_DE[key] || '';
}

async function openGateMail(event, subjectKey, bodyKey) {
  const button = event.currentTarget;
  if (button.disabled) return;
  button.disabled = true;
  try {
    const response = await fetch('/api/auth/contact', {
      credentials: 'same-origin',
      cache: 'no-store',
      signal: AbortSignal.timeout(10000)
    });
    const payload = await response.json();
    if (!response.ok || payload.ok !== true || typeof payload.email !== 'string'
      || !/^[^\s@?&#%]+@[^\s@?&#%]+\.[^\s@?&#%]+$/.test(payload.email)) {
      throw new Error('Contact unavailable');
    }
    if (currentMessageKey === 'kontaktFehler') setAuthMessage(null);
    window.location.href = `mailto:${payload.email}?subject=${encodeURIComponent(gt(subjectKey))}&body=${encodeURIComponent(gt(bodyKey))}`;
  } catch (_error) {
    setAuthMessage('kontaktFehler', 'error');
  } finally {
    button.disabled = false;
  }
}

function getGateElements() {
  return {
    gate: document.getElementById('authGate'),
    form: document.getElementById('authGateForm'),
    loginInput: document.getElementById('authGateLogin'),
    passwordInput: document.getElementById('authGatePassword'),
    rememberInput: document.getElementById('authGateRemember'),
    message: document.getElementById('authGateMessage'),
    submitButton: document.getElementById('authGateSubmit'),
    forgotPasswordLink: document.getElementById('authGateForgotPassword'),
    accessRequestLink: document.getElementById('authGateAccessRequest'),
    languageSwitch: document.getElementById('authGateLanguageSwitch')
  };
}

function setAuthMessage(messageKey, type = '') {
  currentMessageKey = messageKey || null;
  const { message: messageElement } = getGateElements();
  if (!messageElement) return;
  messageElement.textContent = messageKey ? gt(messageKey) : '';
  messageElement.dataset.state = type;
}

function setLoginBusy(isBusy) {
  const { submitButton, loginInput, passwordInput, rememberInput } = getGateElements();
  if (submitButton) submitButton.disabled = isBusy;
  if (loginInput) loginInput.disabled = isBusy;
  if (passwordInput) passwordInput.disabled = isBusy;
  if (rememberInput) rememberInput.disabled = isBusy;
}

function showLoginScreen(messageKey = null) {
  clearCurrentUser();
  document.body.classList.remove('ks-app-ready', 'auth-booting');
  document.body.classList.add('auth-required');
  const { gate, loginInput, passwordInput } = getGateElements();
  if (gate) gate.hidden = false;
  if (passwordInput) passwordInput.value = '';
  setAuthMessage(messageKey, messageKey ? 'error' : '');
  setLoginBusy(false);
  requestAnimationFrame(() => loginInput?.focus());
}

function hideLoginScreen() {
  const { gate } = getGateElements();
  if (gate) gate.hidden = true;
  document.body.classList.remove('auth-booting', 'auth-required');
  document.body.classList.add('ks-app-ready');
}

function mountAppShell() {
  if (appMounted) return true;
  const mount = document.getElementById('ksStudioAppMount');
  const template = document.getElementById('ksStudioAppTemplate');
  if (!mount || !(template instanceof HTMLTemplateElement)) {
    setAuthMessage('vorbereitungFehler', 'error');
    return false;
  }
  mount.replaceChildren(template.content.cloneNode(true));
  appMounted = true;
  return true;
}

// Angewandte Texte auf dem Gate — einmal beim Boot (falls die im
// localStorage gemerkte Sprache nicht DE ist) und wieder nach jedem
// Flaggen-Klick.
function applyGateTexts() {
  document.querySelectorAll('[data-i18n-gate-key]').forEach(element => {
    const key = element.dataset.i18nGateKey;
    if (key) element.textContent = gt(key);
  });
  document.documentElement.lang = gateLanguageCode.toLowerCase();
  if (currentMessageKey) setAuthMessage(currentMessageKey, getGateElements().message?.dataset.state);
}

function renderGateLanguageSwitch() {
  const { languageSwitch } = getGateElements();
  if (!languageSwitch) return;
  languageSwitch.innerHTML = gateLanguages.map(language => {
    const code = String(language.sprache_code || '').trim().toUpperCase();
    if (!code) return '';
    const isActive = code === gateLanguageCode;
    const icon = flagIconSvg(code) || escapeHtml(language.flagge_emoji || code);
    return `
      <button
        type="button"
        class="btn icon-btn auth-gate-language-btn${isActive ? ' is-active' : ''}"
        data-gate-language-code="${escapeHtml(code)}"
        title="${escapeHtml(language.sprache_name || code)}"
        aria-pressed="${isActive ? 'true' : 'false'}"
      >${icon}</button>
    `;
  }).join('');
}

async function loadGateLabels(code) {
  gateLabels = { ...GATE_LABEL_FALLBACK_DE };
  if (code === 'DE') return;
  try {
    const response = await fetch(`/api/auth/gate-labels?lang=${encodeURIComponent(code)}`, { credentials: 'same-origin' });
    const payload = await response.json().catch(() => ({}));
    const labels = payload.labels || {};
    Object.entries(GATE_LABEL_DB_KEYS).forEach(([localKey, dbKey]) => {
      if (labels[dbKey]) gateLabels[localKey] = labels[dbKey];
    });
  } catch (error) {
    console.warn('KS Studio: Login-Beschriftungen konnten nicht geladen werden, es bleibt beim deutschen Text.', error);
  }
}

async function setGateLanguage(code) {
  const nextCode = String(code || 'DE').trim().toUpperCase() || 'DE';
  if (nextCode === gateLanguageCode) return;
  gateLanguageCode = nextCode;
  storeGateLanguage(nextCode);
  renderGateLanguageSwitch();
  await loadGateLabels(nextCode);
  applyGateTexts();
}

async function initGateLanguageSwitch() {
  const { languageSwitch } = getGateElements();
  languageSwitch?.addEventListener('click', event => {
    const button = event.target.closest('[data-gate-language-code]');
    if (button) setGateLanguage(button.dataset.gateLanguageCode);
  });

  try {
    const response = await fetch('/api/auth/gate-languages', { credentials: 'same-origin' });
    const payload = await response.json().catch(() => ({}));
    gateLanguages = Array.isArray(payload.languages) ? payload.languages : [];
  } catch (error) {
    console.warn('KS Studio: Sprachliste fuer den Login-Bildschirm konnte nicht geladen werden.', error);
    gateLanguages = [];
  }
  renderGateLanguageSwitch();

  if (gateLanguageCode !== 'DE') {
    await loadGateLabels(gateLanguageCode);
    applyGateTexts();
  }
}

// Die auf dem Gate gewaehlte Sprache wird NUR bei einem echten Login-Absenden
// (submitLogin() oben, GATE_LANGUAGE_FRESH_LOGIN_KEY) an die eingeloggte App
// weitergereicht — app_options_controller.js' applyStoredAppOptions() liest
// diesen Key einmalig und wendet ihn VOR initI18n() als S.ui.language an
// (siehe dortiger Kommentar). Eine stille Session-Wiederherstellung
// (readCurrentSession() unten, kein Login-Absenden) setzt diesen Key bewusst
// NICHT — sonst wuerde bei jedem Neuladen die alte Gate-Sprache eine
// zwischenzeitlich in den Optionen bewusst gewaehlte Sprache ueberschreiben.

async function readCurrentSession() {
  const response = await fetch('/api/auth/me', {
    method: 'GET',
    credentials: 'same-origin'
  });
  const payload = await response.json().catch(() => ({}));
  if (!response.ok || !payload.user) return null;
  return payload.user;
}

async function startStudio(user) {
  if (appStarted) return;
  setCurrentUser(user);
  if (!mountAppShell()) {
    return;
  }
  hideLoginScreen();
  try {
    await withWorkspaceLoadingOverlay(async () => {
      const maintenance = await import('../js/core/app/release_maintenance_runtime.js');
      maintenance.installReleaseMaintenanceInputGuard();
      const { initApp, registerApplicationModules } = await import('./bootstrap/app_bootstrap.js?v=20260904f');
      await registerApplicationModules();
      try {
        const { installGpuPlanWorkspace } = await import('../js/core/render/gpu_plan_workspace.js');
        installGpuPlanWorkspace();
      } catch (error) { console.warn('[gpu-workspace] WebGL2-Bearbeitung nicht verfügbar:', error); }
      await initApp();
      await maintenance.startReleaseMaintenance();
    }, { label: 'KS Studio wird gestartet...', details: 'Arbeitsbereich wird vorbereitet' });
    appStarted = true;
    // „Was ist neu“ (Katalog-Release K5): blockiert den Start nicht.
    import('../js/core/app/whats_new_runtime.js?v=20261006a')
      .then(module => { module.bindWhatsNewBadge(); return module.maybeShowWhatsNew(); })
      .catch(error => console.warn('„Was ist neu“ nicht verfügbar:', error));
  } catch (error) {
    console.error(error);
    appStarted = false;
    showLoginScreen('ladenFehler');
    throw error;
  }
}

async function tryRestoreSession() {
  setAuthMessage('sessionWirdGeprueft', 'loading');
  try {
    let user = await readCurrentSession();
    if (!user) {
      // Same short-delayed retry as waitForFreshSessionToPropagate() below,
      // applied to the very first /api/auth/me check on a fresh page load —
      // this one previously had no retry at all, so a single transient
      // "no user" response here dropped an already-logged-in visitor back
      // to the login gate for no reason.
      await new Promise(resolve => { setTimeout(resolve, 250); });
      user = await readCurrentSession();
    }
    if (user) {
      await startStudio(user);
      return;
    }
    showLoginScreen(null);
  } catch (error) {
    console.warn('Auth session check failed', error);
    showLoginScreen(null);
  }
}

// Nach einem frischen Login-POST kann der allererste Folge-Request (der
// synchron durch setCurrentUser()'s Listener-Kaskade ausgeloest wird --
// message_runtime.js's Unread-Count-Abruf, i18n-Labels, Context-Menu-Config
// in app_bootstrap.js) noch mit 401 scheitern, obwohl der Login selbst
// bereits erfolgreich war (Nutzer-Meldung 2026-08-26, studio.chappsolut.ch:
// mehrere 401-Fehler in der Konsole direkt nach dem Login). Statt blind zu
// verzoegern, wird hier aktiv (mit kurzem Backoff) bestaetigt, dass
// /api/auth/me die neue Session bereits erkennt, BEVOR startStudio() die
// Listener-Kaskade ausloest -- im Normalfall (keine Verzoegerung) gelingt
// das schon beim ersten Versuch und kostet nur einen zusaetzlichen
// schnellen Roundtrip.
async function waitForFreshSessionToPropagate(maxAttempts = 6, delayMs = 100) {
  for (let attempt = 0; attempt < maxAttempts; attempt++) {
    try {
      const user = await readCurrentSession();
      if (user) return;
    } catch (_error) {
      // Netzwerkfehler wie einen fehlgeschlagenen Versuch behandeln, weiter probieren.
    }
    await new Promise(resolve => { setTimeout(resolve, delayMs); });
  }
}

async function submitLogin(event) {
  event.preventDefault();
  const { loginInput, passwordInput, rememberInput } = getGateElements();
  const login = String(loginInput?.value || '').trim();
  const password = String(passwordInput?.value || '');
  if (!login || !password) {
    setAuthMessage('loginUngueltig', 'error');
    return;
  }

  setLoginBusy(true);
  setAuthMessage('loginWirdGeprueft', 'loading');
  try {
    const response = await fetch('/api/auth/login', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({
        login,
        password,
        remember: rememberInput?.checked === true
      })
    });
    const payload = await response.json().catch(() => ({}));
    if (!response.ok || payload.ok === false || !payload.user) {
      throw new Error('Login oder Passwort ungueltig.');
    }
    try {
      window.localStorage?.setItem(GATE_LANGUAGE_FRESH_LOGIN_KEY, gateLanguageCode);
    } catch (_err) {
      // Ohne Browser-Speicher wird die Gate-Sprache fuer diesen Login einfach nicht uebernommen.
    }
    await waitForFreshSessionToPropagate();
    await startStudio(payload.user);
  } catch (_error) {
    setAuthMessage('loginUngueltig', 'error');
    setLoginBusy(false);
    passwordInput?.focus();
    passwordInput?.select();
  }
}

function boot() {
  initAppVersionBadge();
  const { form, forgotPasswordLink, accessRequestLink } = getGateElements();
  form?.addEventListener('submit', submitLogin);
  forgotPasswordLink?.addEventListener('click', event => openGateMail(event, 'passwortVergessenBetreff', 'passwortVergessenText'));
  accessRequestLink?.addEventListener('click', event => openGateMail(event, 'zugangAnfordernBetreff', 'zugangAnfordernText'));
  initGateLanguageSwitch();
  tryRestoreSession();
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', boot, { once: true });
} else {
  boot();
}
